import { useState, useEffect } from 'react';
import posthog from 'posthog-js';

// Consent gate for analytics cookies (PostHog). Essential cookies — the login
// session — are exempt under EU rules and are not covered by this choice.
//
// This is a real gate, not decoration: PostHog starts opted OUT and is only
// opted in once the visitor accepts. Declining keeps it off permanently.
// ─────────────────────────────────────────────────────────────────────────────
// TEMPORARY, LAUNCH TESTING ONLY — set back to true when done.
//
// false = PostHog captures from everyone and the banner is hidden entirely.
// The banner is hidden rather than left showing, because a banner promising
// "nothing is collected unless you accept" while collecting anyway is worse
// than no banner at all.
//
// Analytics cookies need consent in the EU, so this is a deliberate, temporary
// gap while Leanne verifies the launch is working end to end.
//
// ⚠️ DO NOT FLIP THIS TO true. Leanne's call, 19 August 2026: it stays false
// until she needs real sign-ups flowing first to be completely sure the funnel
// works. It goes back on ONLY when she explicitly says so, in words. Do not
// change it as part of "launch prep", "tidying up", or any other task.
// ─────────────────────────────────────────────────────────────────────────────
const ANALYTICS_REQUIRE_CONSENT = false;

const STORAGE_KEY = 'mimique_cookie_consent'; // 'accepted' | 'declined'

export const readConsent = () => {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
};

/** Persistence to initialise PostHog with, based on any stored choice. */
export const initialPersistence = () =>
    (!ANALYTICS_REQUIRE_CONSENT || readConsent() === 'accepted' ? 'localStorage+cookie' : 'memory');

/** Remove any PostHog cookie/localStorage left over from a previous visit. */
const clearPosthogStorage = () => {
    try {
        document.cookie.split(';').forEach((c) => {
            const name = c.split('=')[0].trim();
            if (name.startsWith('ph_')) {
                document.cookie = `${name}=; Max-Age=0; path=/`;
                document.cookie = `${name}=; Max-Age=0; path=/; domain=.${window.location.hostname}`;
            }
        });
        Object.keys(localStorage)
            .filter((k) => k.startsWith('ph_') || k.includes('posthog'))
            .forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
};

/**
 * Apply a stored choice to PostHog. Called once on boot, straight after init.
 * Memory persistence means a declining visitor gets no PostHog cookie at all —
 * opting out alone still writes a device_id cookie, which is exactly the kind
 * of identifier consent is meant to gate.
 */
export const applyStoredConsent = () => {
    const choice = readConsent();
    if (!ANALYTICS_REQUIRE_CONSENT) {
        try {
            posthog.set_config({ persistence: 'localStorage+cookie' });
            posthog.opt_in_capturing();
        } catch { /* posthog not ready — safe to ignore */ }
        return choice;
    }
    try {
        if (choice === 'accepted') {
            posthog.set_config({ persistence: 'localStorage+cookie' });
            posthog.opt_in_capturing();
        } else {
            posthog.set_config({ persistence: 'memory' });
            posthog.opt_out_capturing();
            clearPosthogStorage();
        }
    } catch { /* posthog not ready — safe to ignore */ }
    return choice;
};

const CookieBanner = () => {
    const [choice, setChoice] = useState(() => readConsent());

    useEffect(() => {
        applyStoredConsent();
    }, []);

    if (!ANALYTICS_REQUIRE_CONSENT || choice) return null;

    const decide = (value) => {
        try { localStorage.setItem(STORAGE_KEY, value); } catch { /* ignore */ }
        try {
            if (value === 'accepted') {
                posthog.set_config({ persistence: 'localStorage+cookie' });
                posthog.opt_in_capturing();
            } else {
                posthog.opt_out_capturing();
                posthog.set_config({ persistence: 'memory' });
                clearPosthogStorage();
            }
        } catch { /* ignore */ }
        setChoice(value);
    };

    return (
        <div
            role="dialog"
            aria-label="Cookie choices"
            style={{
                position: 'fixed',
                left: 16,
                right: 16,
                bottom: 16,
                zIndex: 100,
                maxWidth: 620,
                margin: '0 auto',
                background: '#0a1610',
                color: '#e0f0e8',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 20,
                padding: '18px 20px',
                boxShadow: '0 20px 50px -12px rgba(0,0,0,0.6)',
                fontFamily: "'Inter', sans-serif",
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
            }}
        >
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                We use essential cookies to make Mimique work. We would also like optional analytics
                cookies to see how it is used, so it can be improved. Nothing is collected unless
                you accept.{' '}
                <a href="/legal/privacy" style={{ color: '#7ab896', textDecoration: 'underline' }}>
                    Privacy policy
                </a>
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                    onClick={() => decide('accepted')}
                    style={{
                        border: 'none', cursor: 'pointer', background: '#4da874', color: '#fff',
                        fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700,
                        padding: '10px 20px', borderRadius: 9999,
                    }}
                >
                    Accept analytics
                </button>
                <button
                    onClick={() => decide('declined')}
                    style={{
                        cursor: 'pointer', background: 'transparent', color: '#e0f0e8',
                        border: '1px solid rgba(255,255,255,0.25)',
                        fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600,
                        padding: '10px 20px', borderRadius: 9999,
                    }}
                >
                    Essential only
                </button>
            </div>
        </div>
    );
};

export default CookieBanner;
